<template>
  <div
    :class="{ 'has-value': !!selectedType }"
    class="pt-2"
  >
    <VPDropdown
      input-id="dealType"
      placeholder="Choose Deal Condition"
      :options="dealOptions"
      :disabled="disabled"
      @change="onTypeChange"
    />
  </div>
  <div
    class="flex pt-3 gap-2"
    :class="{ dirty }"
  >
    <template
      v-for="(input, i) in dealInput.inputs"
      :key="i"
    >
      <component
        :is="input.component"
        v-bind="input.binds"
        class="text-left flex w-max text-vp-base-900"
        hide-empty-input
        :disabled="disabled"
        @blur="onBlur"
      />
      <span class="text-vp-base-900">
        {{ dealInputProps[selectedType]?.separator?.[i] || null }}
      </span>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue';
import { useFormsStore } from 'vuniform';
import VPDropdown from '@/components/form/VPDropdown.vue';
import VPPriceInput from '@/components/form/VPPriceInput.vue';
import VPPercentageInput from '@/components/form/VPPercentageInput.vue';
import { DealEnum } from '@/generated/graphql';

const formsStore = useFormsStore();
const formId = inject('formId', '');

interface Props {
  amountInputId: string;
  percentInputId: string;
  typeOfDeal: 'bar' | 'ticket';
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  amountInputId: 'amount',
  percentInputId: 'percent',
});

const dealInput = computed(() => dealInputProps[<string>formsStore?.fieldGetValue(formId, 'dealType')] || {});

const ticketDealOptions = [
  {
    value: null,
    name: 'None',
  },
  {
    value: DealEnum.Gbor,
    name: 'Percentage',
  },
  {
    value: DealEnum.GborAfterExpenses,
    name: 'Percentage after expenses',
  },
  {
    value: DealEnum.ArtistGuarantee,
    name: 'Artist guarantee',
  },
  {
    value: DealEnum.ArtistGuaranteeVsGbor,
    name: 'Artist guarantee vs Percentage',
  },
  {
    value: DealEnum.ArtistGuaranteeVsGborAfterExpenses,
    name: 'Artist guarantee vs Percentage after expenses',
  },
  {
    value: DealEnum.ArtistGuaranteePlusGborAfterExpenses,
    name: 'Artist guarantee plus. Percentage after expenses',
  },
];

const barDealOptions = [
  {
    value: DealEnum.FlatFee,
    name: 'Flat fee to artist',
  },
  {
    value: DealEnum.GuaranteeToVenue,
    name: 'Bar Guarantee due to Venue',
  },
  {
    value: DealEnum.PercentAfterHit,
    name: '% of F&B sales if F&B exceeds amount after amount',
  },
  {
    value: DealEnum.PercentWithHit,
    name: '% of F&B sales if F&B exceeds amount',
  },
  {
    value: DealEnum.Rental,
    name: 'Rental fee to Venue',
  },
  {
    value: DealEnum.SalesPercentage,
    name: '% of F&B sales',
  },
];

const dealOptions = computed(() => {
  switch (props.typeOfDeal) {
    case 'bar':
      return barDealOptions;
    case 'ticket':
      return ticketDealOptions;
  }
});

const dealInputProps = {
  [DealEnum.Gbor]: {
    inputs: [
      {
        component: VPPercentageInput,
        binds: { inputId: props.percentInputId, placeholder: 'Add percentage (%)' },
      },
    ],
  },
  [DealEnum.GborAfterExpenses]: {
    inputs: [
      {
        component: VPPercentageInput,
        binds: {
          inputId: props.percentInputId,
          class: 'vp-long-field',
          placeholder: 'Add percentage after expenses (%)',
        },
      },
    ],
  },
  [DealEnum.ArtistGuarantee]: {
    inputs: [
      {
        component: VPPriceInput,
        binds: { inputId: props.amountInputId, placeholder: 'Add artist guarantee ($)' },
      },
    ],
  },
  [DealEnum.ArtistGuaranteeVsGbor]: {
    inputs: [
      {
        component: VPPriceInput,
        binds: { inputId: props.amountInputId, placeholder: 'Add artist guarantee ($)' },
      },
      {
        component: VPPercentageInput,
        binds: { inputId: props.percentInputId, placeholder: 'Add percentage (%)' },
      },
    ],
    separator: ['vs.'],
  },
  [DealEnum.ArtistGuaranteeVsGborAfterExpenses]: {
    inputs: [
      {
        component: VPPriceInput,
        binds: { inputId: props.amountInputId, placeholder: 'Add artist guarantee ($)' },
      },
      {
        component: VPPercentageInput,
        binds: {
          inputId: props.percentInputId,
          class: 'vp-long-field',
          placeholder: 'Add percentage after expenses (%)',
        },
      },
    ],
    separator: ['vs.'],
  },
  [DealEnum.ArtistGuaranteePlusGborAfterExpenses]: {
    inputs: [
      {
        component: VPPriceInput,
        binds: { inputId: props.amountInputId, placeholder: 'Add artist guarantee ($)' },
      },
      {
        component: VPPercentageInput,
        binds: {
          inputId: props.percentInputId,
          class: 'vp-long-field',
          placeholder: 'Add percentage after expenses (%)',
        },
      },
    ],
    separator: ['plus.'],
  },
  [DealEnum.FlatFee]: {
    inputs: [
      {
        component: VPPriceInput,
        binds: { inputId: props.amountInputId, placeholder: 'Add flat fee ($)' },
      },
    ],
  },
  [DealEnum.Rental]: {
    inputs: [
      {
        component: VPPriceInput,
        binds: { inputId: props.amountInputId, placeholder: 'Add rental fee ($)' },
      },
    ],
  },
  [DealEnum.GuaranteeToVenue]: {
    inputs: [
      {
        component: VPPriceInput,
        binds: { inputId: props.amountInputId, placeholder: 'Add bar guarantee ($)' },
      },
    ],
  },
  [DealEnum.SalesPercentage]: {
    inputs: [
      {
        component: VPPercentageInput,
        binds: { inputId: props.percentInputId, placeholder: 'Add percentage (%)' },
      },
    ],
  },
  [DealEnum.PercentWithHit]: {
    inputs: [
      {
        component: VPPercentageInput,
        binds: {
          inputId: props.percentInputId,
          class: 'vp-long-field',
          placeholder: 'Add percentage of F&B sales (%)',
        },
      },
      {
        component: VPPriceInput,
        binds: { inputId: 'hitAmount', placeholder: 'Add amount ($)' },
      },
    ],
    separator: ['if F&B exceeds'],
  },
  [DealEnum.PercentAfterHit]: {
    inputs: [
      {
        component: VPPercentageInput,
        binds: {
          inputId: props.percentInputId,
          class: 'vp-long-field',
          placeholder: 'Add percentage of F&B sales (%)',
        },
      },
      {
        component: VPPriceInput,
        binds: { inputId: 'hitAmount', placeholder: 'Add amount ($)' },
      },
      {
        component: VPPriceInput,
        binds: { inputId: 'firstAmount', placeholder: 'Add amount ($)' },
      },
    ],
    separator: ['if F&B exceeds', 'after the first'],
  },
};

const dirty = computed(() => formsStore.isFormDirty(formId));

const selectedType = computed(() => formsStore.fieldGetValue(formId, 'dealType'));

const emit = defineEmits<{
  (e: 'change'): void;
}>();

const onBlur = () => {
  emit('change');
};

const onTypeChange = () => {
  emit('change');
};
</script>
